// Atmósfera estándar internacional (ISO 2533). Ver docs/theory/atmosphere.md.

export const G0 = 9.80665;
export const R_AIR = 287.05287;
export const GAMMA = 1.4;
export const T0 = 288.15;
export const P0 = 101325;
const EARTH_RADIUS = 6_356_766;
const SUTHERLAND_BETA = 1.458e-6;
const SUTHERLAND_S = 110.4;

export interface AirState {
  /** Temperatura (K). */
  readonly temperature: number;
  /** Presión (Pa). */
  readonly pressure: number;
  /** Densidad (kg/m³). */
  readonly density: number;
  /** Velocidad del sonido (m/s). */
  readonly speedOfSound: number;
  /** Viscosidad dinámica (kg/(m·s)). */
  readonly dynamicViscosity: number;
  /** Viscosidad cinemática (m²/s). */
  readonly kinematicViscosity: number;
}

interface Layer {
  readonly base: number; // altura geopotencial de la base (m)
  readonly lapse: number; // K/m
  readonly tBase: number;
  readonly pBase: number;
}

const LAPSES: ReadonlyArray<readonly [number, number]> = [
  [0, -0.0065],
  [11_000, 0],
  [20_000, 0.001],
  [32_000, 0.0028],
  [47_000, 0],
  [51_000, -0.0028],
];
const H_MIN = -5_000;
const H_MAX = 71_000;

const pressureInLayer = (tb: number, pb: number, lapse: number, dh: number, t: number): number =>
  lapse !== 0 ? pb * Math.pow(t / tb, -G0 / (R_AIR * lapse)) : pb * Math.exp((-G0 * dh) / (R_AIR * tb));

// Construye las bases de capa integrando desde el nivel del mar.
const LAYERS: readonly Layer[] = (() => {
  const layers: Layer[] = [];
  let tBase = T0;
  let pBase = P0;
  for (let i = 0; i < LAPSES.length; i++) {
    const [base, lapse] = LAPSES[i]!;
    layers.push({ base, lapse, tBase, pBase });
    const next = i + 1 < LAPSES.length ? LAPSES[i + 1]![0] : H_MAX;
    const dh = next - base;
    const tTop = tBase + lapse * dh;
    pBase = pressureInLayer(tBase, pBase, lapse, dh, tTop);
    tBase = tTop;
  }
  return layers;
})();

export const geopotentialAltitude = (geometric: number): number =>
  (EARTH_RADIUS * geometric) / (EARTH_RADIUS + geometric);

/** Temperatura y presión ISA a altura geométrica `h` (m sobre el nivel del mar). */
export function isaTemperaturePressure(h: number): { temperature: number; pressure: number } {
  const H = Math.min(Math.max(geopotentialAltitude(h), H_MIN), H_MAX);
  let layer = LAYERS[0]!;
  for (const candidate of LAYERS) {
    if (H >= candidate.base) layer = candidate;
  }
  const dh = H - layer.base;
  const temperature = layer.tBase + layer.lapse * dh;
  const pressure = pressureInLayer(layer.tBase, layer.pBase, layer.lapse, dh, temperature);
  return { temperature, pressure };
}

export function airState(temperature: number, pressure: number): AirState {
  const density = pressure / (R_AIR * temperature);
  const dynamicViscosity =
    (SUTHERLAND_BETA * Math.pow(temperature, 1.5)) / (temperature + SUTHERLAND_S);
  return {
    temperature,
    pressure,
    density,
    speedOfSound: Math.sqrt(GAMMA * R_AIR * temperature),
    dynamicViscosity,
    kinematicViscosity: dynamicViscosity / density,
  };
}

export interface AtmosphereOptions {
  /** Altitud del sitio sobre el nivel del mar (m). Por defecto 0. */
  siteAltitude?: number;
  /** Temperatura medida en el sitio (K). Por defecto, la ISA a esa altitud. */
  siteTemperature?: number;
  /** Presión medida en el sitio (Pa). Por defecto, la ISA a esa altitud. */
  sitePressure?: number;
}

export interface Atmosphere {
  /** Estado del aire a altitud `h` sobre el nivel del mar (m). */
  at(h: number): AirState;
}

/** Atmósfera ISA con desfase de temperatura y factor de presión constantes (ver nota teórica). */
export function createAtmosphere(options: AtmosphereOptions = {}): Atmosphere {
  const hs = options.siteAltitude ?? 0;
  const siteIsa = isaTemperaturePressure(hs);
  const dT = (options.siteTemperature ?? siteIsa.temperature) - siteIsa.temperature;
  const k = (options.sitePressure ?? siteIsa.pressure) / siteIsa.pressure;
  return {
    at(h: number): AirState {
      const isa = isaTemperaturePressure(h);
      return airState(isa.temperature + dT, isa.pressure * k);
    },
  };
}
